import { Buildings, Key, Trash, X } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	useAssignMeterToApartment,
	useUnassignMeterFromApartment,
	useUpdateMeterEncryptionKey,
} from "@/api/districts/mutations";
import { useApartments } from "@/api/districts/queries";
import { SetEncryptionKeyDialog } from "@/components/meters/set-encryption-key-dialog";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { useDistrict } from "@/contexts/district-context";

type MeterBulkActionsProps = {
	selectedSerials: string[];
	onSuccess: () => void;
	onClear: () => void;
};

export function MeterBulkActions({
	selectedSerials,
	onSuccess,
	onClear,
}: MeterBulkActionsProps) {
	const { t } = useTranslation();
	const [assignOpen, setAssignOpen] = useState(false);
	const [search, setSearch] = useState("");
	const { districtId } = useDistrict();

	const { data: apartmentsData } = useApartments({
		district_id: districtId ?? undefined,
		page_size: 1000,
	});
	const apartments = apartmentsData?.results ?? [];

	const filtered = useMemo(() => {
		if (!search) return apartments;
		const q = search.toLowerCase();
		return apartments.filter(
			(a) =>
				a.street.toLowerCase().includes(q) ||
				a.apartment_number.toLowerCase().includes(q),
		);
	}, [apartments, search]);

	const assignMutation = useAssignMeterToApartment();
	const unassignMutation = useUnassignMeterFromApartment();
	const encryptionKeyMutation = useUpdateMeterEncryptionKey();
	const [keyDialogOpen, setKeyDialogOpen] = useState(false);

	const isPending =
		assignMutation.isPending ||
		unassignMutation.isPending ||
		encryptionKeyMutation.isPending;
	const count = selectedSerials.length;

	function handleRemove() {
		Promise.all(
			selectedSerials.map((serialNumber) =>
				unassignMutation.mutateAsync({ serialNumber }),
			),
		)
			.then(() => {
				toast.success(t("meterBulkActions.removeSuccess", { count }));
				onSuccess();
			})
			.catch(() => {
				toast.error(t("meterBulkActions.removeError"));
			});
	}

	function handleAssign(apartmentId: number) {
		setAssignOpen(false);
		setSearch("");
		Promise.all(
			selectedSerials.map((serialNumber) =>
				assignMutation.mutateAsync({ serialNumber, apartment: apartmentId }),
			),
		)
			.then(() => {
				toast.success(t("meterBulkActions.assignSuccess", { count }));
				onSuccess();
			})
			.catch(() => {
				toast.error(t("meterBulkActions.assignError"));
			});
	}

	function handleSetEncryptionKey(encryptionKey: string) {
		Promise.all(
			selectedSerials.map((serialNumber) =>
				encryptionKeyMutation.mutateAsync({ serialNumber, encryptionKey }),
			),
		)
			.then(() => {
				toast.success(t("meterBulkActions.encryptionKeySuccess", { count }));
				setKeyDialogOpen(false);
				onSuccess();
			})
			.catch(() => {
				toast.error(t("meterBulkActions.encryptionKeyError"));
			});
	}

	return (
		<div
			inert={count === 0 ? true : undefined}
			className={`flex h-full w-full items-center justify-between bg-muted px-4 transition-opacity duration-150 ${
				count === 0
					? "pointer-events-none opacity-0"
					: "pointer-events-auto opacity-100"
			}`}
		>
			<div className="flex items-center gap-2">
				<Button
					variant="ghost"
					size="icon"
					className="-ml-2 size-7 shrink-0"
					onClick={onClear}
					aria-label={t("meterBulkActions.clearSelection")}
				>
					<X className="size-3.5" aria-hidden="true" />
				</Button>
				<p className="text-sm text-muted-foreground whitespace-nowrap">
					<span className="font-semibold text-foreground tabular-nums">
						{count}
					</span>{" "}
					{t("meterBulkActions.selected")}
				</p>
			</div>

			<div className="flex items-center gap-2">
				<AlertDialog>
					<AlertDialogTrigger asChild>
						<Button
							variant="ghost"
							className="gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
							disabled={isPending}
						>
							<Trash className="size-4 shrink-0" aria-hidden="true" />
							<span className="hidden sm:inline">
								{t("meterBulkActions.removeFromApartment")}
							</span>
						</Button>
					</AlertDialogTrigger>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{t("meterBulkActions.removeTitle")}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{t("meterBulkActions.removeDescription", { count })}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>
								{t("meterBulkActions.cancel")}
							</AlertDialogCancel>
							<AlertDialogAction
								className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
								onClick={handleRemove}
							>
								{t("meterBulkActions.confirm")}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>

				<div className="h-4 w-px bg-border" />

				<Popover
					open={assignOpen}
					onOpenChange={(open) => {
						setAssignOpen(open);
						if (!open) setSearch("");
					}}
				>
					<PopoverTrigger asChild>
						<Button variant="outline" className="gap-2" disabled={isPending}>
							<Buildings className="size-4 shrink-0" aria-hidden="true" />
							<span className="hidden sm:inline">
								{t("meterBulkActions.assignToApartment")}
							</span>
						</Button>
					</PopoverTrigger>
					<PopoverContent align="end" className="w-56 p-1">
						<div className="p-1">
							<Input
								placeholder={t("meters.searchApartment")}
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className="h-8 text-sm"
							/>
						</div>
						<ul className="max-h-52 overflow-y-auto">
							{filtered.length > 0 ? (
								filtered.map((a) => (
									<li key={a.id}>
										<button
											type="button"
											className="w-full rounded px-3 py-2 text-left text-sm hover:bg-accent"
											onClick={() => handleAssign(a.id)}
										>
											{a.street} {a.apartment_number}
										</button>
									</li>
								))
							) : (
								<li className="text-muted-foreground px-3 py-2 text-sm">
									{t("meterBulkActions.noApartments")}
								</li>
							)}
						</ul>
					</PopoverContent>
				</Popover>

				<div className="h-4 w-px bg-border" />

				<Button
					variant="outline"
					className="gap-2"
					disabled={isPending}
					onClick={() => setKeyDialogOpen(true)}
				>
					<Key className="size-4 shrink-0" aria-hidden="true" />
					<span className="hidden sm:inline">
						{t("meterBulkActions.setEncryptionKey")}
					</span>
				</Button>
			</div>
			<SetEncryptionKeyDialog
				open={keyDialogOpen}
				onOpenChange={setKeyDialogOpen}
				onSubmit={handleSetEncryptionKey}
				pending={encryptionKeyMutation.isPending}
				count={count}
			/>
		</div>
	);
}
